import { useRef, useState } from "react";
import classNames from 'classnames';
import { debounce } from "underscore";
import { Validation } from "./text";
import Icon, { COLORS, ICONS } from "../icon";
import { useCallback } from "react";
import { useEffect } from "react";
import { OptionalComponent } from "../../../utils/optional-component";

interface IBasicInputProps {
	debounceTime?: number;
	defaultText?: string;
	focus?: boolean;
	label?: string;
	margin?: 'none' | 'normal';
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
	onDebounce?: (value: string) => void;
	required?: boolean;
	setFocus?: React.Dispatch<React.SetStateAction<boolean>>;
	tooltip?: () => JSX.Element;
	valid?: Validation;

}

const BasicInput: React.FC<IBasicInputProps & React.HTMLProps<HTMLInputElement>> = ({
	debounceTime = 300,
	defaultText = '',
	focus,
	label,
	margin = 'normal',
	onBlur = () => null,
	onChange = () => null,
	onDebounce = () => null,
	required = false,
	setFocus,
	tooltip,
	type = 'text',
	valid = Validation.normal,
	...rest
}) => {
	const [value, setValue] = useState(defaultText);
	const [showPassword, setShowPassword] = useState(false);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const passwordTimer = useRef<NodeJS.Timeout | null>(null);

	const debounced = useRef(debounce((value: string) => {
		onDebounce(value);
	}, debounceTime)).current;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		debounced(e.target.value);
		onChange(e);
	};

	const togglePassword = useCallback((e: any) => {
		e.preventDefault();
		setShowPassword(!showPassword);
	}, [showPassword]);

	useEffect(() => {
		if (passwordTimer.current) clearTimeout(passwordTimer.current);

		if (showPassword) {
			passwordTimer.current = setTimeout(() => {
				setShowPassword(false);
			}, 3000);
		}
	}, [showPassword]);


	useEffect(() => {
		if (inputRef?.current && focus) {
			inputRef.current.focus();
		}
	}, [focus]);

	// if the parent passes down the method setFocus, then we want to make
	// sure we set it to false when the user blurs - note: we cannot use reacts onBlur method
	// because it will not trigger under every scenario
	useEffect(() => {
		const referencedRef = inputRef?.current;
		const handleBlur = () => {
			setFocus && setFocus(false);
		};
		if (referencedRef) {
			referencedRef.addEventListener('blur', handleBlur);
		}
		return () => {
			if (referencedRef) {
				referencedRef.removeEventListener('blur', handleBlur);
			}
		};
	}, [inputRef, setFocus]);

	return (
		<div className={`field-group text normal ${margin === 'none' ? 'no-margin' : ''} ${valid}`}>
			{label ? (
				<div
					style={{
						display: 'flex',
						alignItems: 'center'
					}}
				>
					<label
						className={classNames(
							"evt-field-label",
							{ "evt-field-label-required": required },
						)}
					>
						{label}
					</label>
					<OptionalComponent display={!!tooltip}>
						<div style={{ marginLeft: '5px' }}>{tooltip?.()}</div>
					</OptionalComponent>
				</div>
			) : null}
			<input
				ref={inputRef}
				type={showPassword ? 'text' : type}
				onChange={handleChange}
				onBlur={onBlur}
				value={value}
				{...rest}
			/>

			{type === 'password' && (
				<button onMouseDown={togglePassword} type="button" onTouchStart={togglePassword} className="no-style show-password"><Icon name={ICONS.VIEWERS_EYE} size={12} color={COLORS.WHITE} /></button>
			)}
		</div>
	);
};

export default BasicInput;
